import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { motion } from 'framer-motion';

interface Service {
  id: string;
  name: string;
  price: number;
  partner_id: string;
  partners: { name: string, logo_url?: string };
}

export default function AdminAllServices() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('partner_services')
      .select('*, partners(name, logo_url)')
      .order('name', { ascending: true });
    
    if (data) setServices(data);
    setLoading(false);
  };

  const updatePrice = async (id: string, newPrice: number) => {
    await supabase.from('partner_services').update({ price: newPrice }).eq('id', id);
    fetchServices();
  };

  const filteredServices = services.filter(s => 
    s.name.toLowerCase().includes(search.toLowerCase()) || 
    s.partners.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-brand-black text-2xl">Banco Global de Servicios</h2>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">Gestiona todos los precios y servicios ofrecidos a través de tus partners.</p>
        </div>
        <div className="relative">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-brand-gray-400" />
          <input 
            type="text" 
            placeholder="Buscar por servicio o marca..." 
            className="pl-11 pr-6 py-2.5 bg-white border border-brand-gray-200 rounded-full text-sm font-sans focus:outline-none focus:border-brand-black w-72 shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-brand-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-brand-gray-50 border-b border-brand-gray-100">
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Servicio</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Marca Partner</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Precio (€)</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-gray-100">
            {filteredServices.map((service) => (
              <tr key={service.id} className="hover:bg-brand-gray-50/50 transition-colors">
                <td className="px-6 py-4">
                  <p className="font-sans text-brand-black font-medium text-sm">{service.name}</p>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-brand-cream flex items-center justify-center overflow-hidden border border-brand-gray-100">
                      {service.partners.logo_url ? <img src={service.partners.logo_url} alt="" className="w-full h-full object-cover" /> : <i className="ri-building-line text-[10px] text-brand-sage" />}
                    </div>
                    <span className="font-sans text-xs text-brand-gray-600">{service.partners.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <input 
                      type="number" 
                      className="w-20 bg-brand-gray-50 border border-brand-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-sage"
                      value={service.price}
                      onChange={(e) => updatePrice(service.id, Number(e.target.value))}
                    />
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <button 
                    onClick={async () => {
                      if (window.confirm('¿Eliminar este servicio?')) {
                        await supabase.from('partner_services').delete().eq('id', service.id);
                        fetchServices();
                      }
                    }}
                    className="p-2 text-brand-gray-300 hover:text-red-500 transition-colors"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredServices.length === 0 && (
          <div className="py-20 text-center font-sans text-brand-gray-400 italic">No se han encontrado servicios.</div>
        )}
      </div>
    </div>
  );
}
